// Web → Analytika → Tržby z FAPI.
//
// Děkovací stránka umí do měření poslat jen to, co jí FAPI předá v adrese:
// variabilní symbol ano, částku ne. Web si proto dosadí ceníkovou cenu
// varianty. Jakmile přibude bump nebo upsell, přestane to sedět.
//
// Tohle okno podle variabilního symbolu dohledá skutečnou fakturu ve FAPI
// a ukáže, kde se změřená částka liší od zaplacené. Nejdřív jen ukáže,
// zapisuje se až na druhé kliknutí. Původní změřená částka se přitom
// nezahazuje, uloží se vedle, takže jde vždycky zpátky.
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Receipt, X, RefreshCw } from "lucide-react";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { apiFetch } from "../../lib/api.js";

const kc = (n) => `${Math.round(Number(n) || 0).toLocaleString("cs-CZ")} Kč`;
const den = (ts) => {
  try {
    const d = new Date(ts);
    return `${d.getDate()}. ${d.getMonth() + 1}.`;
  } catch (e) {
    return "";
  }
};

const BARVA_STAVU = {
  "sedí": "text-emerald-600",
  "liší se": "text-amber-600",
  "faktura nenalezena": "text-rose-600",
  "chybí variabilní symbol": "text-slate-400",
};

// 14. 9. 2026 (fáze 6): okno jde otevřít i zvenku (z nabídky v hlavičce).
// Když stránka předá `open` a `onClose`, tlačítko se nekreslí a stav řídí ona.
export default function TrzbyFapi({ open, onClose }) {
  const uzka = useUzkaObrazovka();
  const rizene = typeof open === "boolean";
  const [vlastniOtevreno, setOtevreno] = useState(false);
  const otevreno = rizene ? open : vlastniOtevreno;
  const [bezi, setBezi] = useState(false);
  const [data, setData] = useState(null);
  const [chyba, setChyba] = useState(null);
  const [hotovo, setHotovo] = useState(null);

  const nacti = async (zapsat) => {
    setBezi(true);
    setChyba(null);
    if (zapsat) setHotovo(null);
    try {
      const r = await apiFetch(`/api/web-trzby-fapi?dni=365${zapsat ? "&zapsat=1" : ""}`, { method: "POST" });
      const j = await r.json();
      if (!r.ok || j.error) throw new Error(j.error || `Chyba ${r.status}`);
      setData(j);
      if (zapsat) {
        setHotovo(
          j.upraveno > 0
            ? `Přepsáno ${j.upraveno} nákupů podle faktur. Rozdíl proti změřenému je ${kc(j.rozdilKc)}.`
            : "Nic přepisovat nebylo potřeba, změřené částky sedí s fakturami."
        );
      }
    } catch (e) {
      setChyba(e.message);
    } finally {
      setBezi(false);
    }
  };

  const otevri = () => {
    setOtevreno(true);
    setData(null);
    setHotovo(null);
    setChyba(null);
    nacti(false);
  };

  // Řízené otevření zvenku: stejný postup jako klik na vlastní tlačítko.
  useEffect(() => {
    if (!rizene || !open) return;
    setData(null);
    setHotovo(null);
    setChyba(null);
    nacti(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rizene, open]);

  const zavri = () => {
    if (bezi) return;
    if (rizene) onClose?.();
    else setOtevreno(false);
  };

  const radky = data?.radky || [];
  const kZapisu = radky.filter((r) => r.stav === "liší se").length;

  return (
    <>
      {!rizene && (
        <button
          type="button"
          onClick={otevri}
          title="Porovnat změřené tržby se skutečnými fakturami ve FAPI"
          className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-sm font-medium text-white/90 transition hover:bg-white/20 min-h-[44px] sm:min-h-0"
        >
          <Receipt className="h-4 w-4" /> Tržby z FAPI
        </button>
      )}

      {otevreno &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-brand-dark/50 p-4 backdrop-blur-sm pb-[max(1rem,env(safe-area-inset-bottom))]"
            onClick={(e) => {
              if (e.target === e.currentTarget) zavri();
            }}
          >
            <div
              className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-2xl bg-white p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-start gap-4">
                <div className="flex-shrink-0 rounded-xl bg-brand-100 p-3 text-brand-700">
                  <Receipt className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-semibold leading-tight text-slate-900">Tržby podle faktur ve FAPI</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">
                    Web umí do měření zapsat jen ceníkovou cenu, protože částku mu FAPI v adrese nepředá.
                    Tady se ke každému nákupu dohledá skutečná faktura podle variabilního symbolu.
                  </p>
                </div>
                <button onClick={zavri} className="text-slate-400 transition hover:text-slate-600" aria-label="Zavřít">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {chyba && (
                <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{chyba}</div>
              )}
              {hotovo && (
                <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                  {hotovo}
                </div>
              )}

              {bezi && !data && (
                <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
                  <RefreshCw className="h-5 w-5 animate-spin" /> Stahuji faktury z FAPI, chvilku to trvá…
                </div>
              )}

              {data && (
                <>
                  <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                    <span>
                      Nákupů za rok <strong className="text-slate-900">{data.nakupu}</strong>
                    </span>
                    <span aria-hidden className="h-3 w-px bg-slate-300" />
                    <span>
                      Spárováno <strong className="text-slate-900">{data.sparovano}</strong>
                    </span>
                    <span aria-hidden className="h-3 w-px bg-slate-300" />
                    <span>
                      Faktur ve FAPI <strong className="text-slate-900">{data.fakturCelkem}</strong>
                    </span>
                  </div>

                  {/* 14.9.2026 (fáze 6b): na telefonu seznam řádků, tabulka
                      si vynucovala 620 bodů a rolovala se do stran. */}
                  {uzka ? (
                  <div className="min-h-0 flex-1 divide-y divide-slate-100 overflow-auto rounded-xl border border-slate-200">
                    {radky.map((r) => (
                      <div key={r.id} className="px-3 py-2.5">
                        <div className="flex items-baseline gap-2">
                          <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-slate-900">
                            {r.varianta || "neznámo"}
                          </span>
                          <span className="shrink-0 text-[15px] font-bold tabular-nums text-slate-900">
                            {r.skutecne == null ? kc(r.zmereno) : kc(r.skutecne)}
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[13px] text-slate-500">
                          <span>{den(r.kdy)}</span>
                          {(r.vs || r.orderId) && <span className="font-mono">{r.vs || r.orderId}</span>}
                          <span className={BARVA_STAVU[r.stav] || "text-slate-500"}>{r.stav}</span>
                        </div>
                      </div>
                    ))}
                    {!radky.length && (
                      <div className="px-3 py-6 text-center text-sm text-slate-400">Zatím nic.</div>
                    )}
                  </div>
                  ) : (
                  <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200">
                    <table className="w-full min-w-[620px] text-sm">
                      <thead className="sticky top-0 bg-slate-50">
                        <tr className="text-left text-slate-500">
                          <th className="px-3 py-2 font-medium">Kdy</th>
                          <th className="px-3 py-2 font-medium">Varianta</th>
                          <th className="px-3 py-2 font-medium">VS</th>
                          <th className="px-3 py-2 text-right font-medium">Změřeno</th>
                          <th className="px-3 py-2 text-right font-medium">Podle faktury</th>
                          <th className="px-3 py-2 font-medium">Stav</th>
                        </tr>
                      </thead>
                      <tbody>
                        {radky.map((r) => (
                          <tr key={r.id} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-slate-700">{den(r.kdy)}</td>
                            <td className="px-3 py-2 text-slate-600">{r.varianta || "neznámo"}</td>
                            <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.vs || r.orderId || ""}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-700">{kc(r.zmereno)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-900">
                              {r.skutecne == null ? "" : kc(r.skutecne)}
                            </td>
                            <td className={`px-3 py-2 ${BARVA_STAVU[r.stav] || "text-slate-500"}`}>{r.stav}</td>
                          </tr>
                        ))}
                        {!radky.length && (
                          <tr>
                            <td colSpan={6} className="py-8 text-center text-slate-400">
                              Za poslední rok zatím žádný nákup
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  )}

                  <p className="mt-3 text-xs leading-relaxed text-slate-500">
                    „Faktura nenalezena" znamená, že k nákupu sice máme variabilní symbol, ale ve FAPI k němu
                    faktura není. U vlastních testovacích průchodů děkovací stránkou je to normální.
                  </p>
                </>
              )}

              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  onClick={zavri}
                  disabled={bezi}
                  className="btn btn-secondary"
                >
                  Zavřít
                </button>
                <button
                  onClick={() => nacti(true)}
                  disabled={bezi || !kZapisu}
                  title={kZapisu ? "" : "Není co přepisovat"}
                  className="btn btn-primary"
                >
                  {bezi && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />}
                  {kZapisu ? `Přepsat ${kZapisu} podle faktur` : "Nic k přepsání"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
